import { useMemo, useState, useEffect, useCallback } from 'react';

import LockRoundedIcon from '@mui/icons-material/LockRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Box,
  List,
  Alert,
  Button,
  Dialog,
  Divider,
  ListItem,
  TextField,
  Typography,
  DialogTitle,
  ListItemText,
  DialogActions,
  DialogContent,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import MuiIcon from 'src/components/mui-icon';

import { connectivityCoordinator } from './connectivity-service';
import {
  openWifiSettings,
  scanWifiNetworks,
  type WifiNetwork,
  connectWifiNetwork,
  getWifiCapabilities,
} from './wifi-connection';

type Props = { open: boolean; onClose: VoidFunction };
type DialogState = 'idle' | 'scanning' | 'connecting' | 'connected' | 'error';

function signalLabel(level: number): string {
  if (level >= -55) return 'Excellent';
  if (level >= -67) return 'Good';
  if (level >= -75) return 'Fair';
  return 'Weak';
}

export function WifiConnectionDialog({ open, onClose }: Props) {
  const capabilities = useMemo(() => getWifiCapabilities(), []);
  const [networks, setNetworks] = useState<WifiNetwork[]>([]);
  const [selected, setSelected] = useState<WifiNetwork | null>(null);
  const [password, setPassword] = useState('');
  const [state, setState] = useState<DialogState>('idle');
  const [error, setError] = useState<string | null>(null);

  const scan = useCallback(async () => {
    if (!capabilities.canScan) return;
    setState('scanning');
    setError(null);
    try {
      setNetworks(await scanWifiNetworks());
      setState('idle');
    } catch (scanError) {
      setState('error');
      setError(
        scanError instanceof Error
          ? scanError.message
          : 'Nearby networks could not be read. Check Wi-Fi and location permissions.'
      );
    }
  }, [capabilities.canScan]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setPassword('');
    setError(null);
    setState('idle');
    if (capabilities.canScan) scan().catch(() => undefined);
  }, [capabilities.canScan, open, scan]);

  const handleConnect = async () => {
    const targetSsid = selected?.ssid ?? '';
    if (!targetSsid) {
      setError('Enter a Wi-Fi network name.');
      return;
    }
    setState('connecting');
    setError(null);
    try {
      await connectWifiNetwork({
        ssid: targetSsid,
        password: password || undefined,
        security: selected?.security ?? (password ? 'WPA2' : 'OPEN'),
      });
      setState('connected');
      window.setTimeout(() => {
        connectivityCoordinator.retryNow().catch(() => undefined);
      }, 1200);
    } catch (connectError) {
      setState('error');
      setError(
        connectError instanceof Error ? connectError.message : 'The network could not be joined.'
      );
    }
  };

  const handleOpenSettings = () => {
    openWifiSettings().catch((settingsError) => {
      setError(
        settingsError instanceof Error
          ? settingsError.message
          : 'Open Wi-Fi settings from your operating system.'
      );
    });
  };

  const needsPassword = selected ? selected.security !== 'OPEN' : true;
  const platformMessage =
    capabilities.platform === 'ios'
      ? 'iOS manages network selection in Settings. Open Settings, return to the main Settings menu, select Wi-Fi, connect to a network, then return to PointCraft.'
      : 'Browsers cannot discover or join Wi-Fi networks. Open Wi-Fi settings on this device, connect, then return and retry.';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Connect to Wi-Fi</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {!capabilities.canScan && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">{platformMessage}</Alert>
          </Box>
        )}

        {capabilities.canScan && (
          <>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Nearby networks
              </Typography>
              <Button
                size="small"
                startIcon={
                  state === 'scanning' ? (
                    <CircularProgress size={15} />
                  ) : (
                    <MuiIcon icon={RefreshRoundedIcon} width={17} />
                  )
                }
                disabled={state === 'scanning'}
                onClick={() => scan().catch(() => undefined)}
              >
                Scan again
              </Button>
            </Box>
            <Divider />
            {networks.length === 0 && state !== 'scanning' ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                  No networks were returned. Turn on Wi-Fi and location services, or open system
                  settings.
                </Alert>
              </Box>
            ) : (
              <List disablePadding>
                {networks.map((network) => (
                  <ListItem key={network.ssid} disablePadding divider>
                    <ListItemButton
                      selected={selected?.ssid === network.ssid}
                      onClick={() => {
                        setSelected(network);
                        setPassword('');
                      }}
                    >
                      <MuiIcon icon={WifiRoundedIcon} width={22} sx={{ mr: 2 }} />
                      <ListItemText
                        primary={network.ssid}
                        secondary={`${signalLabel(network.signalLevel)} signal · ${network.security}`}
                      />
                      {network.isConnected && (
                        <MuiIcon icon={CheckCircleRoundedIcon} width={20} color="success.main" />
                      )}
                      {network.security !== 'OPEN' && (
                        <MuiIcon icon={LockRoundedIcon} width={17} sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
            {selected && needsPassword && (
              <Box sx={{ p: 3 }}>
                <TextField
                  fullWidth
                  autoFocus
                  label={`Password for ${selected.ssid}`}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Box>
            )}
          </>
        )}

        {state === 'connecting' && (
          <Alert icon={<CircularProgress size={19} />} severity="info" sx={{ m: 2 }}>
            Waiting for your device to approve and join the network…
          </Alert>
        )}
        {state === 'connected' && (
          <Alert severity="success" sx={{ m: 2 }}>
            Connection request accepted. PointCraft is checking its services now.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {capabilities.platform === 'android' && (
          <Button
            onClick={handleOpenSettings}
            startIcon={<MuiIcon icon={SettingsRoundedIcon} width={18} />}
          >
            Open settings
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        {capabilities.platform === 'ios' && (
          <Button
            variant="contained"
            onClick={handleOpenSettings}
            startIcon={<MuiIcon icon={SettingsRoundedIcon} width={18} />}
          >
            Open Settings
          </Button>
        )}
        {capabilities.canConnectKnownNetwork && (
          <Button
            variant="contained"
            disabled={state === 'connecting' || state === 'connected'}
            onClick={() => handleConnect().catch(() => undefined)}
          >
            Connect
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
